import React, { createContext, useState, useContext, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { useAuth } from './AuthContext';
import { auth } from '../lib/firebase';
import * as utils from '../lib/utils';

const enforceInactiveLogout = async () => {
  try {
    localStorage.removeItem('userData');
    await signOut(auth);
  } catch (e) {
    console.error('UserDataContext: error during inactive sign-out:', e);
  }
  // ProtectedRoute will <Navigate> to /login (react-router) once Firebase
  // clears currentUser — no full-page refresh. If we were not already on
  // /login when the inactivity was detected, leave a sessionStorage flag so
  // the login page can surface the reason.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    sessionStorage.setItem('napco-inactive-redirect', '1');
  }
};

// Create the context
const UserDataContext = createContext(null);

// Custom hook to use the user data context
export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

// Provider component
export const UserDataProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);

  // Helper function to save user data to localStorage
  const saveUserDataToLocalStorage = (data) => {
    if (data) {
      // console.log("Saving User Data to localStorage:", data);
      localStorage.setItem('userData', JSON.stringify({
        ...data,
        timestamp: new Date().getTime() // Add timestamp for cache validation
      }));
    } else {
      localStorage.removeItem('userData');
    }
  };

  // Helper function to set user data and save to localStorage
  const updateUserData = (data) => {
    setUserData(data);
    saveUserDataToLocalStorage(data);
  };

  // Fetch user data whenever component mounts or currentUser changes
  useEffect(() => {
    // console.log("UserDataContext: Effect triggered, currentUser:", currentUser?.email);
    
    const fetchUserData = async () => {
      // Reset state
      setIsLoading(true);
      setError(null);
      
      if (!currentUser || !currentUser.uid) {
        updateUserData(null);
        setIsLoading(false);
        return;
      }

      try {
        // First load cached data from localStorage as a temporary placeholder
        // This provides faster initial rendering while we fetch fresh data
        try {
          const storedData = localStorage.getItem('userData');
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            setUserData(parsedData); // Don't update localStorage, just show temporarily
          }
        } catch (e) {
          console.error("UserDataContext: Error loading from localStorage:", e);
        }

        let fetchedUserData = (await utils.getPeopleByFireauthId(currentUser.uid)).data.peoples[0] || null;

        // Fallback: if the row hasn't been stamped yet (race with the login
        // flow's first-login stamp), find it by email so the freshly logged-in
        // user isn't bounced. The next poll will pick up the stamped row.
        if (!fetchedUserData && currentUser.email) {
          fetchedUserData = (await utils.getPeopleByEmail(currentUser.email)).data.peoples[0] || null;
        }

        if (!fetchedUserData || fetchedUserData.status !== 'Active') {
          await enforceInactiveLogout();
          return;
        }

        // Update with fresh data
        updateUserData(fetchedUserData);
      } catch (error) {
        console.error('UserDataContext: Error fetching user data:', error);
        setError('Failed to fetch user data');

        // If API call fails, fall back to cached data but mark as potentially stale.
        // If the cached data isn't Active, do not let the user proceed — sign them out.
        const storedData = localStorage.getItem('userData');
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            if (parsedData.status !== 'Active') {
              await enforceInactiveLogout();
              return;
            }
            setUserData({
              ...parsedData,
              potentiallyStale: true // Mark data as potentially stale
            });
          } catch (e) {
            console.error("UserDataContext: Error parsing cached data:", e);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]); // Only depend on currentUser

  // Provide a method to manually refresh user data
  const refreshUserData = async () => {
    if (!currentUser || !currentUser.uid) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await utils.getPeopleByFireauthId(currentUser.uid);
      const fetchedUserData = response.data.peoples[0] || null;

      if (!fetchedUserData || fetchedUserData.status !== 'Active') {
        await enforceInactiveLogout();
        return;
      }

      updateUserData(fetchedUserData);
    } catch (error) {
      console.error('UserDataContext: Error refreshing user data:', error);
      setError('Failed to refresh user data');
    } finally {
      setIsLoading(false);
    }
  };

  // Background poll so admin-driven Inactive flips propagate to open tabs
  // without requiring a user action. Runs every 5 min and on tab refocus.
  // Doesn't touch isLoading — silent refresh, no UI flash.
  useEffect(() => {
    if (!currentUser?.uid) return;
    // const POLL_MS = 5 * 60 * 1000;
    // For testing: poll every 30 seconds to verify that it works.
    const POLL_MS = 30 * 1000;
    const tick = async () => {
      try {
        console.log('UserDataContext: Polling for user data...');
        const response = await utils.getPeopleByFireauthId(currentUser.uid);
        const fetchedUserData = response.data.peoples[0] || null;
        if (!fetchedUserData || fetchedUserData.status !== 'Active') {
          await enforceInactiveLogout();
          return;
        }
        updateUserData(fetchedUserData);
      } catch (err) {
        console.error('UserDataContext: poll error:', err);
      }
    };
    const id = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [currentUser]);

  // console.log("UserDataContext: Current state:", { userData, isLoading, error });

  const value = {
    userData,
    isLoading,
    error,
    refreshUserData
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
